import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, FlaskConical, Plus, Trash2, Eraser, ShieldAlert } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

export default function OilAdminTestAccounts() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kako_id: '', label: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetForm, setResetForm] = useState({ confirm_kako_id: '', reason: '' });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/oil/test-accounts');
      setData(r.data);
    } catch { toast.error('Erro ao carregar as contas de teste.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.kako_id.trim() || form.label.trim().length < 2 || form.reason.trim().length < 3) {
      toast.error('Preencha KAKO ID, apelido e motivo.'); return;
    }
    setBusy(true);
    try {
      const r = await api.post('/admin/oil/test-accounts', form);
      toast.success(`Conta de teste marcada: ${r.data.account.kako_nickname || form.kako_id}`);
      setForm({ kako_id: '', label: '', reason: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao marcar a conta.'); }
    setBusy(false);
  };

  const remove = async (acc) => {
    const reason = window.prompt(`Motivo para desmarcar ${acc.label} (${acc.kako_id}):`);
    if (!reason || reason.trim().length < 3) { toast.error('Motivo obrigatório.'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/oil/test-accounts/${acc.kako_id}/remove`, { reason });
      toast.success('Conta desmarcada — volta a jogar normalmente.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao desmarcar.'); }
    setBusy(false);
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/oil/test-accounts/${resetFor.kako_id}/reset`, resetForm);
      toast.success(`Progresso apagado: ${fmt(r.data.total)} registro(s) removido(s).`);
      setResetFor(null);
      setResetForm({ confirm_kako_id: '', reason: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao zerar a conta.'); }
    setBusy(false);
  };

  if (!data) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="oil-admin-test-accounts">
      <div className="rounded-xl border border-violet-300 bg-violet-50/60 p-3">
        <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase text-violet-800">
          <FlaskConical className="h-3.5 w-3.5" /> Contas de teste
        </p>
        <p className="text-[11px] leading-relaxed text-violet-900">
          Conta real marcada aqui joga o OIL TYCOON normalmente para conferir todas as funções,
          mas fica fora dos rankings, da premiação, do jackpot e das listas públicas. Nos serviços
          sujos, o único alvo dela é ela mesma — nenhum jogador real é afetado.
        </p>
      </div>

      <div className="rounded-xl border p-3">
        <p className="mb-2 text-xs font-black uppercase text-slate-600">Marcar uma conta</p>
        <div className="grid gap-2 sm:grid-cols-[140px_1fr_1fr_auto]">
          <label className="text-[10px] font-bold text-slate-500">
            <span className="block">KAKO ID</span>
            <input value={form.kako_id} data-testid="oil-test-account-kako-id"
              onChange={(e) => setForm((p) => ({ ...p, kako_id: e.target.value }))}
              className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
          </label>
          <label className="text-[10px] font-bold text-slate-500">
            <span className="block">Apelido / observação</span>
            <input value={form.label} data-testid="oil-test-account-label"
              placeholder="Ex.: conta da FOX"
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm" />
          </label>
          <label className="text-[10px] font-bold text-slate-500">
            <span className="block">Motivo (obrigatório)</span>
            <input value={form.reason} data-testid="oil-test-account-reason"
              placeholder="Ex.: conferir missões e máfia"
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              className="mt-0.5 h-9 w-full rounded-lg border px-2 text-xs" />
          </label>
          <div className="flex items-end">
            <Button size="sm" disabled={busy} onClick={add} data-testid="oil-test-account-add"
              className="bg-violet-600 text-white hover:bg-violet-700">
              <Plus className="mr-1 h-3.5 w-3.5" /> Marcar
            </Button>
          </div>
        </div>
        <p className="mt-1 text-[10px] text-slate-400">
          O nome da conta é confirmado no KAKO antes de marcar.
        </p>
      </div>

      <div className="rounded-xl border p-3">
        <p className="mb-2 text-xs font-black uppercase text-slate-600">
          Contas marcadas ({data.total})
        </p>
        {data.accounts.length === 0 ? (
          <p className="text-[11px] text-slate-400" data-testid="oil-test-accounts-empty">
            Nenhuma conta de teste marcada.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="oil-test-accounts-table">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 pr-2">Conta</th>
                  <th className="py-2 pr-2">Apelido</th>
                  <th className="py-2 pr-2">Marcada em</th>
                  <th className="py-2 pr-2">Season atual</th>
                  <th className="py-2 pr-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.kako_id} className="border-b hover:bg-slate-50"
                    data-testid={`oil-test-account-row-${a.kako_id}`}>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        {a.avatar
                          ? <img src={a.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                          : <div className="h-7 w-7 rounded-full bg-slate-200" />}
                        <div className="min-w-0">
                          <p className="truncate font-bold">{a.kako_nickname || 'Conta KAKO'}</p>
                          <p className="text-[10px] text-slate-400">{a.kako_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      <p className="font-semibold text-slate-700">{a.label}</p>
                      <p className="text-[10px] text-slate-400">{a.reason}</p>
                    </td>
                    <td className="py-2 pr-2 text-slate-500">{fmtDate(a.created_at)}</td>
                    <td className="py-2 pr-2">
                      {a.plays_current_season ? (
                        <span className="text-[11px] text-slate-600">
                          jogando · 💎{fmt(a.current_progress?.black_coins)} BC ·
                          {' '}{fmt(a.current_progress?.fox_dollars)} FOX
                        </span>
                      ) : <span className="text-[11px] text-slate-400">ainda não entrou</span>}
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" disabled={busy}
                          data-testid={`oil-test-account-reset-${a.kako_id}`}
                          onClick={() => { setResetFor(a); setResetForm({ confirm_kako_id: '', reason: '' }); }}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50">
                          <Eraser className="mr-1 h-3.5 w-3.5" /> Zerar
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy}
                          data-testid={`oil-test-account-remove-${a.kako_id}`}
                          onClick={() => remove(a)}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Desmarcar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetFor && (
        <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-3" data-testid="oil-test-account-reset-box">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase text-rose-800">
            <ShieldAlert className="h-3.5 w-3.5" /> Zerar o progresso de {resetFor.label}
          </p>
          <p className="mb-2 text-[11px] leading-relaxed text-rose-900">
            Apaga moedas, instalações, barris, extrações, missões, sequência, ataques e histórico
            <b> somente desta conta</b> ({resetFor.kako_id}). Nenhum outro jogador é tocado. A conta
            dela na loja continua igual. Para confirmar, digite o KAKO ID.
          </p>
          <div className="grid gap-2 sm:grid-cols-[160px_1fr_auto]">
            <input value={resetForm.confirm_kako_id} data-testid="oil-test-account-reset-confirm"
              placeholder="Digite o KAKO ID"
              onChange={(e) => setResetForm((p) => ({ ...p, confirm_kako_id: e.target.value }))}
              className="h-9 w-full rounded-lg border px-2 text-sm font-bold" />
            <input value={resetForm.reason} data-testid="oil-test-account-reset-reason"
              placeholder="Motivo (obrigatório)"
              onChange={(e) => setResetForm((p) => ({ ...p, reason: e.target.value }))}
              className="h-9 w-full rounded-lg border px-2 text-xs" />
            <div className="flex gap-1">
              <Button size="sm" disabled={busy} onClick={doReset}
                data-testid="oil-test-account-reset-confirm-btn"
                className="bg-rose-600 text-white hover:bg-rose-700">
                <Eraser className="mr-1 h-3.5 w-3.5" /> Apagar progresso
              </Button>
              <Button size="sm" variant="outline" onClick={() => setResetFor(null)}
                data-testid="oil-test-account-reset-cancel">Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
